import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Typography,
  Select,
  DatePicker,
  Button,
  Statistic,
  Tag,
  Progress,
  Tooltip,
  message,
  Modal,
  Space,
  Alert,
  Collapse,
  Spin,
  Divider,
} from "antd";
import { Line, Pie } from "react-chartjs-2";
import {
  DollarOutlined,
  PieChartOutlined,
  FundOutlined,
  BankOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  BookOutlined,
  VideoCameraOutlined,
  BulbOutlined,
} from "@ant-design/icons";

import { AppContext } from "../context/AppContext";
import { normalizeTermOptions } from "../utils/loanUtils";
import axios from "axios";
import moment from "moment";

// Custom CSS for tour highlight animation
const tourStyles = `
  @keyframes pulse-border {
    0% {
      box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(24, 144, 255, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(24, 144, 255, 0);
    }
  }
  
  .tour-highlight {
    animation: pulse-border 2s infinite;
    border-radius: 4px;
    transition: all 0.3s ease-in-out;
  }
`;

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ProfitabilityAnalysis = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "year"),
    moment(),
  ]);
  const [productFilter, setProductFilter] = useState("all");
  const [termFilter, setTermFilter] = useState("all");
  const [profitData, setProfitData] = useState([]);

  // State for real data
  const [loanProducts, setLoanProducts] = useState([]);
  const [loans, setLoans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [profitMetrics, setProfitMetrics] = useState({
    netInterestMargin: 0,
    returnOnAssets: 0,
    costToIncome: 0,
    riskAdjustedMargin: 0,
  });
  const [costBreakdown, setCostBreakdown] = useState({
    operational: 45,
    funding: 30,
    risk: 15,
    acquisition: 10,
  });

  // Tour and help-related state
  const [helpVisible, setHelpVisible] = useState(false);
  const [guideTipVisible, setGuideTipVisible] = useState(true);
  const [activeTourStep, setActiveTourStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [showTip, setShowTip] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!company?.id || !token) return;

      try {
        setLoading(true);

        // Get date range for filtering
        const startDate = dateRange[0].format("YYYY-MM-DD");
        const endDate = dateRange[1].format("YYYY-MM-DD");

        // Fetch loan products
        const productsResponse = await axios.get(
          `https://zedlenders.pickmesms.com/items/loan_products?filter[company]=${company.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Fetch active loans with relationships
        const loansResponse = await axios.get(
          `https://zedlenders.pickmesms.com/items/loans?filter[company]=${company.id}&filter[loan_status][_in]=7,3,9&fields=*,loan_product.*,loan_transactions.*`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Fetch all transactions
        const transactionsResponse = await axios.get(
          `https://zedlenders.pickmesms.com/items/transactions?filter[company]=${company.id}&filter[transaction_date][_between]=${startDate},${endDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const loanProducts = productsResponse.data.data;
        const allLoans = loansResponse.data.data;
        const allTransactions = transactionsResponse.data.data;

        setLoanProducts(loanProducts);
        setLoans(allLoans);
        setTransactions(allTransactions);

        // Process data for profitability analysis
        processData(loanProducts, allLoans, allTransactions);
      } catch (error) {
        console.error(
          "Failed to fetch data for profitability analysis:",
          error
        );
        message.error("Failed to load profitability data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company?.id, token, dateRange]);

  // Function to process loan data and calculate profitability metrics
  const processData = (loanProducts, loans, transactions) => {
    // Create a map of product IDs to their analysis data
    const productMap = {};

    // Initialize product map with all loan products
    loanProducts.forEach((product) => {
      productMap[product.id] = {
        id: product.id,
        product: product.loan_name,
        totalLoans: 0,
        interestIncome: 0,
        operatingCosts: 0,
        chargeOffs: 0,
        netProfit: 0,
        term: (() => {
          const termOptions = normalizeTermOptions(product.loan_term_options);
          if (termOptions.length > 0) {
            return `${termOptions.join(", ")} ${product.term_unit}`;
          }
          return `${product.loan_term} ${product.term_unit}`;
        })(),
        interestRate: `${product.interest_rate}%`,
      };
    });

    // Process loans and their transactions
    loans.forEach((loan) => {
      if (!loan.loan_product) return;

      const productId = loan.loan_product.id;
      if (!productMap[productId]) return;

      // Add loan amount to total for this product
      const loanAmount = parseFloat(loan.amount) || 0;
      productMap[productId].totalLoans += loanAmount;

      // Calculate interest income from loan repayments
      const repayments =
        loan.loan_transactions?.filter(
          (t) => t.transaction_type === "Loan Repayment"
        ) || [];

      // For a simple calculation, estimate interest based on the loan's interest rate
      const totalPaid = repayments.reduce(
        (sum, t) => sum + (parseFloat(t.amount) || 0),
        0
      );

      const interestRate = parseFloat(loan.loan_product.interest_rate) || 0;

      // Estimate interest as a portion of total payments based on interest rate
      // This is a simplification - in reality, interest would be calculated based on amortization
      const estimatedInterest = totalPaid * (interestRate / 100);

      // Add to interest income
      productMap[productId].interestIncome += estimatedInterest;

      // Add processing fees to interest income if they exist
      if (
        loan.loan_product.processing_fee &&
        loan.loan_product.processing_fee > 0
      ) {
        let processingFee = 0;
        if (loan.loan_product.processing_fee_type === "percentage") {
          processingFee =
            loanAmount * (parseFloat(loan.loan_product.processing_fee) / 100);
        } else if (loan.loan_product.processing_fee_type === "fixed") {
          processingFee = parseFloat(loan.loan_product.processing_fee);
        }
        productMap[productId].interestIncome += processingFee;
      }

      // If loan has zero interest income but has an amount, add a minimum estimated interest
      // This ensures we have some data to show even for new loans
      if (productMap[productId].interestIncome === 0 && loanAmount > 0) {
        productMap[productId].interestIncome =
          loanAmount * (interestRate / 100) * 0.5; // 50% of potential interest
      }

      // Calculate charge-offs for defaulted loans
      if (loan.loan_status === 9) {
        // Defaulted status
        const outstandingBalance =
          loanAmount - (parseFloat(loan.paid_amount) || 0);
        productMap[productId].chargeOffs +=
          outstandingBalance > 0 ? outstandingBalance : 0;
      }
    });

    // Allocate operating costs proportionally based on loan portfolio size
    const totalLoanAmount = Object.values(productMap).reduce(
      (sum, p) => sum + p.totalLoans,
      0
    );

    // Estimate total operating costs as 30% of total interest income
    // with a minimum based on total loan amount to ensure we have some costs
    const totalInterestIncome = Object.values(productMap).reduce(
      (sum, p) => sum + p.interestIncome,
      0
    );

    // Use either 30% of interest income or 5% of total loan amount, whichever is greater
    const estimatedTotalOperatingCosts = Math.max(
      totalInterestIncome * 0.3,
      totalLoanAmount * 0.05
    );

    // Allocate operating costs and calculate net profit
    Object.values(productMap).forEach((product) => {
      if (totalLoanAmount > 0) {
        // Allocate costs proportionally based on loan amount
        product.operatingCosts =
          estimatedTotalOperatingCosts * (product.totalLoans / totalLoanAmount);

        // Ensure each product has at least some minimal operating costs if it has loans
        if (product.totalLoans > 0 && product.operatingCosts === 0) {
          product.operatingCosts = product.totalLoans * 0.02; // Minimum 2% of loan amount as operating costs
        }
      }

      // Calculate net profit
      product.netProfit =
        product.interestIncome - product.operatingCosts - product.chargeOffs;
    });

    // Calculate key profitability metrics
    const totalAssets = totalLoanAmount;
    const totalIncome = totalInterestIncome;
    const totalCosts = estimatedTotalOperatingCosts;
    const totalChargeOffs = Object.values(productMap).reduce(
      (sum, p) => sum + p.chargeOffs,
      0
    );

    // Net Interest Margin = Interest Income / Total Loan Amount
    const nim = totalLoanAmount > 0 ? (totalIncome / totalLoanAmount) * 100 : 0;

    // Return on Assets = Net Profit / Total Assets
    const roa =
      totalAssets > 0
        ? ((totalIncome - totalCosts - totalChargeOffs) / totalAssets) * 100
        : 0;

    // Cost to Income Ratio = Operating Costs / Income
    const cir = totalIncome > 0 ? (totalCosts / totalIncome) * 100 : 0;

    // Risk-Adjusted Margin = (Income - Charge-offs) / Total Loan Amount
    const ram =
      totalLoanAmount > 0
        ? ((totalIncome - totalChargeOffs) / totalLoanAmount) * 100
        : 0;

    // Update metrics state
    setProfitMetrics({
      netInterestMargin: parseFloat(nim.toFixed(1)),
      returnOnAssets: parseFloat(roa.toFixed(1)),
      costToIncome: parseFloat(cir.toFixed(1)),
      riskAdjustedMargin: parseFloat(ram.toFixed(1)),
    });

    // Extract the products array for the table
    const productsArray = Object.values(productMap);

    // Apply filters if needed
    const filtered = productsArray.filter((p) => {
      return (
        (productFilter === "all" || p.product === productFilter) &&
        (termFilter === "all" || p.term === termFilter)
      );
    });

    setProfitData(filtered);
  };

  // Secondary effect to filter the data when filters change
  useEffect(() => {
    if (loanProducts.length > 0 && loans.length > 0) {
      processData(loanProducts, loans, transactions);
    }
  }, [productFilter, termFilter]);

  // Tour steps for interactive guide
  const tourSteps = [
    {
      title: "Profitability Analysis",
      content:
        "This tool helps you analyze the profitability of your loan portfolio by product and time period.",
      target: ".profit-header",
    },
    {
      title: "Date Range Selection",
      content:
        "Change the date range to analyze profitability for different time periods.",
      target: ".date-range",
    },
    {
      title: "Key Performance Metrics",
      content:
        "These cards show your most important profitability metrics for your lending business.",
      target: ".key-metrics",
    },
    {
      title: "Revenue vs Costs Chart",
      content:
        "This chart compares your monthly interest income against operating costs over time.",
      target: ".revenue-chart",
    },
    {
      title: "Cost Breakdown",
      content:
        "This pie chart shows how your costs are distributed across different categories.",
      target: ".cost-breakdown",
    },
    {
      title: "Product Profitability",
      content:
        "This table provides detailed profitability data for each loan product.",
      target: ".product-table",
    },
  ];

  // Function to generate revenue chart data
  const generateRevenueChart = () => {
    if (!transactions || transactions.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: "Interest Income",
            data: [],
            borderColor: "#1890ff",
            backgroundColor: "#1890ff33",
            tension: 0.4,
          },
          {
            label: "Operating Costs",
            data: [],
            borderColor: "#ff4d4f",
            backgroundColor: "#ff4d4f33",
            tension: 0.4,
          },
        ],
      };
    }

    // Get unique months in the date range
    const startMonth = dateRange[0].clone().startOf("month");
    const endMonth = dateRange[1].clone().startOf("month");
    const months = [];
    let currentMonth = startMonth.clone();

    while (currentMonth.isSameOrBefore(endMonth)) {
      months.push(currentMonth.clone());
      currentMonth.add(1, "month");
    }

    // Generate monthly labels
    const labels = months.map((m) => m.format("MMM YYYY"));

    // Calculate monthly interest income and costs
    const interestIncomeData = [];
    const operatingCostsData = [];

    months.forEach((month) => {
      const monthStart = month.clone().startOf("month");
      const monthEnd = month.clone().endOf("month");

      // Calculate interest income for this month
      const monthlyRepayments = transactions.filter(
        (tx) =>
          tx.transaction_type === "Loan Repayment" &&
          moment(tx.transaction_date).isBetween(
            monthStart,
            monthEnd,
            null,
            "[]"
          )
      );

      // Sum repayment amounts (simplified - in reality we'd separate principal and interest)
      // For this app, we'll estimate that 30% of repayments are interest
      const totalRepayments = monthlyRepayments.reduce(
        (sum, tx) => sum + (Number(tx.amount) || 0),
        0
      );
      const estimatedInterestIncome = totalRepayments * 0.3;

      // Add processing fees received in this month
      const processingFees = transactions
        .filter(
          (tx) =>
            tx.transaction_type === "Processing Fee" &&
            moment(tx.transaction_date).isBetween(
              monthStart,
              monthEnd,
              null,
              "[]"
            )
        )
        .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

      const totalMonthlyIncome = estimatedInterestIncome + processingFees;
      interestIncomeData.push(Math.round(totalMonthlyIncome / 1000)); // Convert to thousands

      // Estimate operating costs as approximately 30% of income
      const estimatedMonthlyCosts = totalMonthlyIncome * 0.3;
      operatingCostsData.push(Math.round(estimatedMonthlyCosts / 1000)); // Convert to thousands
    });

    return {
      labels,
      datasets: [
        {
          label: "Interest Income",
          data: interestIncomeData,
          borderColor: "#1890ff",
          backgroundColor: "#1890ff33",
          tension: 0.4,
        },
        {
          label: "Operating Costs",
          data: operatingCostsData,
          borderColor: "#ff4d4f",
          backgroundColor: "#ff4d4f33",
          tension: 0.4,
        },
      ],
    };
  };

  // Generate revenue chart data
  const revenueChart = generateRevenueChart();

  // Generate product performance chart data
  const generateProductPerformance = () => {
    if (!profitData || profitData.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: "Net Profit Margin",
            data: [],
            backgroundColor: [
              "#1890ff",
              "#52c41a",
              "#722ed1",
              "#fa8c16",
              "#13c2c2",
            ],
          },
        ],
      };
    }

    return {
      labels: profitData.map((p) => p.product),
      datasets: [
        {
          label: "Net Profit Margin",
          data: profitData.map((p) =>
            p.interestIncome > 0
              ? ((p.netProfit / p.interestIncome) * 100).toFixed(1)
              : 0
          ),
          backgroundColor: profitData.map((_, index) => {
            const colors = [
              "#1890ff",
              "#52c41a",
              "#722ed1",
              "#fa8c16",
              "#13c2c2",
            ];
            return colors[index % colors.length];
          }),
        },
      ],
    };
  };

  const productPerformance = generateProductPerformance();

  // Effect to handle highlighting of tour elements
  useEffect(() => {
    // Create style element for the tour styles
    const styleEl = document.createElement("style");
    styleEl.id = "tour-highlight-styles";
    styleEl.innerHTML = tourStyles;
    document.head.appendChild(styleEl);

    if (showTour && tourSteps[activeTourStep]) {
      const targetSelector = tourSteps[activeTourStep].target;
      const targetElement = document.querySelector(targetSelector);

      // Remove any existing highlight classes
      document.querySelectorAll(".tour-highlight").forEach((el) => {
        el.classList.remove("tour-highlight");
        el.style.position = "";
        el.style.zIndex = "";
      });

      // Add highlight to current target element
      if (targetElement) {
        targetElement.classList.add("tour-highlight");
        targetElement.style.position = "relative";
        targetElement.style.zIndex = "1000";

        // Scroll element into view if needed
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      // Clean up highlights when tour ends
      document.querySelectorAll(".tour-highlight").forEach((el) => {
        el.classList.remove("tour-highlight");
        el.style.position = "";
        el.style.zIndex = "";
      });
    }

    // Clean up function for when component unmounts or effect re-runs
    return () => {
      document.querySelectorAll(".tour-highlight").forEach((el) => {
        el.classList.remove("tour-highlight");
        el.style.position = "";
        el.style.zIndex = "";
      });

      // Remove the style element
      const styleElement = document.getElementById("tour-highlight-styles");
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [showTour, activeTourStep]);

  // Interactive tour component
  const renderTourStep = () => {
    if (!showTour) return null;

    const currentStep = tourSteps[activeTourStep];

    return (
      <Modal
        title={currentStep.title}
        open={showTour}
        footer={[
          <Button
            key="back"
            onClick={() => setActiveTourStep(Math.max(0, activeTourStep - 1))}
            disabled={activeTourStep === 0}
          >
            Previous
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setShowTour(false);
              // Clean up is handled by the effect
            }}
          >
            Exit Tour
          </Button>,
          <Button
            key="next"
            type="primary"
            onClick={() => {
              if (activeTourStep === tourSteps.length - 1) {
                setShowTour(false);
                // Clean up is handled by the effect
                message.success(
                  "Tour completed! You can restart it anytime from the help menu."
                );
              } else {
                setActiveTourStep(activeTourStep + 1);
              }
            }}
          >
            {activeTourStep === tourSteps.length - 1 ? "Finish" : "Next"}
          </Button>,
        ]}
        maskClosable={false}
        closable={false}
        style={{
          position: "fixed",
          top: "auto",
          left: "auto",
          right: "20px",
          bottom: "20px",
          transform: "none",
        }}
        width={400}
      >
        <Paragraph>{currentStep.content}</Paragraph>
      </Modal>
    );
  };

  // Help tooltip component
  const HelpTooltip = ({ title, children }) => (
    <Tooltip title={title} color="#108ee9">
      <span style={{ marginLeft: 4, cursor: "help" }}>
        {children || <QuestionCircleOutlined style={{ color: "#8c8c8c" }} />}
      </span>
    </Tooltip>
  );

  // Help Modal
  const renderHelpModal = () => {
    return (
      <Modal
        title={
          <div>
            <BookOutlined style={{ marginRight: 8 }} />
            Profitability Analysis Guide
          </div>
        }
        open={helpVisible}
        onCancel={() => setHelpVisible(false)}
        footer={[
          <Button key="video" type="default" icon={<VideoCameraOutlined />}>
            Watch Tutorial Video
          </Button>,
          <Button key="close" onClick={() => setHelpVisible(false)}>
            Close
          </Button>,
          <Button
            key="tour"
            type="primary"
            onClick={() => {
              setHelpVisible(false);
              setShowTour(true);
            }}
          >
            Take Interactive Tour
          </Button>,
        ]}
        width={800}
      >
        <Collapse defaultActiveKey={["1"]} expandIconPosition="right">
          <Panel header="Overview & Purpose" key="1">
            <Paragraph>
              The Profitability Analysis tool helps you evaluate the financial
              performance of your loan portfolio by product, time period, and
              other key dimensions. Use this report to:
            </Paragraph>
            <ul>
              <li>Identify your most profitable loan products</li>
              <li>Track performance trends over time</li>
              <li>Optimize your product mix based on profitability</li>
              <li>Make informed decisions about product pricing and costs</li>
            </ul>
          </Panel>

          <Panel header="Understanding Key Metrics" key="2">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" title="Net Interest Margin">
                  <Text>
                    The difference between interest income and interest
                    expenses, expressed as a percentage of earning assets.
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="green">{">15%"}: Excellent</Tag>
                    <Tag color="gold">10-15%: Good</Tag>
                    <Tag color="red">{"<10%"}: Below Target</Tag>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Return on Assets">
                  <Text>
                    Net profit divided by total assets, shows how efficiently
                    your capital is being utilized.
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="green">{">4%"}: Strong</Tag>
                    <Tag color="gold">2-4%: Moderate</Tag>
                    <Tag color="red">{"<2%"}: Weak</Tag>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Cost to Income Ratio">
                  <Text>
                    Operating costs divided by income, lower is better as it
                    indicates operational efficiency.
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="green">{"<35%"}: Efficient</Tag>
                    <Tag color="gold">35-45%: Average</Tag>
                    <Tag color="red">{">45%"}: Inefficient</Tag>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Risk-Adjusted Margin">
                  <Text>
                    Net interest margin adjusted for loan losses, gives a more
                    accurate picture of true profitability.
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color="green">{">12%"}: Strong</Tag>
                    <Tag color="gold">8-12%: Adequate</Tag>
                    <Tag color="red">{"<8%"}: Concerning</Tag>
                  </div>
                </Card>
              </Col>
            </Row>
          </Panel>

          <Panel header="Reading the Charts" key="3">
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title="Revenue vs Costs Trend">
                  <ul style={{ paddingLeft: 16 }}>
                    <li>
                      <Text strong>Blue line:</Text> Interest Income
                    </li>
                    <li>
                      <Text strong>Red line:</Text> Operating Costs
                    </li>
                    <li>
                      Growing gap between lines indicates improving
                      profitability
                    </li>
                    <li>
                      Narrowing gap suggests potential profit margin compression
                    </li>
                  </ul>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Cost Breakdown">
                  <ul style={{ paddingLeft: 16 }}>
                    <li>
                      Shows relative proportion of different cost categories
                    </li>
                    <li>Operational: Staff, premises, technology costs</li>
                    <li>Funding: Cost of capital, interest expense</li>
                    <li>Risk: Loan losses, provisions, compliance costs</li>
                    <li>Acquisition: Marketing, sales commissions</li>
                  </ul>
                </Card>
              </Col>
            </Row>
          </Panel>

          <Panel header="Product Profitability Analysis" key="4">
            <Paragraph>
              The Loan Product Profitability table provides detailed insights:
            </Paragraph>
            <ul>
              <li>
                <Text strong>Total Loans:</Text> Total principal amount of loans
                issued
              </li>
              <li>
                <Text strong>Interest Income:</Text> Revenue from interest and
                fees
              </li>
              <li>
                <Text strong>Operating Costs:</Text> Expenses allocated to this
                product
              </li>
              <li>
                <Text strong>Charge-offs:</Text> Loan losses from defaults
              </li>
              <li>
                <Text strong>Net Profit:</Text> Interest Income minus Costs and
                Charge-offs
              </li>
              <li>
                <Text strong>Performance:</Text> ROI as percentage of loan
                amount
              </li>
            </ul>
            <Paragraph>
              <Text strong>Tip:</Text> Use the filters at the top of the table
              to analyze specific products or loan terms.
            </Paragraph>
          </Panel>

          <Panel header="Best Practices" key="5">
            <Paragraph>For effective profitability management:</Paragraph>
            <ul>
              <li>Review profitability monthly and compare against targets</li>
              <li>
                Analyze performance by product, adjust pricing or discontinue
                underperforming products
              </li>
              <li>
                Watch cost-to-income ratio trends to maintain operational
                efficiency
              </li>
              <li>
                Compare risk-adjusted return across products to ensure proper
                risk pricing
              </li>
              <li>
                Consider both absolute profit and relative margin when making
                decisions
              </li>
            </ul>
          </Panel>
        </Collapse>
      </Modal>
    );
  };

  const columns = [
    {
      title: "Loan Product",
      dataIndex: "product",
      sorter: (a, b) => a.product.localeCompare(b.product),
    },
    {
      title: "Total Loans",
      dataIndex: "totalLoans",
      render: (val) => `ZMW ${val.toLocaleString()}`,
      sorter: (a, b) => a.totalLoans - b.totalLoans,
    },
    {
      title: "Interest Income",
      dataIndex: "interestIncome",
      render: (val) => `ZMW ${val.toLocaleString()}`,
      sorter: (a, b) => a.interestIncome - b.interestIncome,
    },
    {
      title: "Costs",
      children: [
        {
          title: "Operating",
          dataIndex: "operatingCosts",
          render: (val) => `ZMW ${val.toLocaleString()}`,
        },
        {
          title: "Charge-offs",
          dataIndex: "chargeOffs",
          render: (val) => `ZMW ${val.toLocaleString()}`,
        },
      ],
    },
    {
      title: "Net Profit",
      dataIndex: "netProfit",
      render: (val, record) => {
        // Calculate profit margin only if interestIncome > 0
        const margin =
          record.interestIncome > 0
            ? ((val / record.interestIncome) * 100).toFixed(1)
            : 0;

        // Determine color based on margin
        const color =
          parseFloat(margin) > 50
            ? "green"
            : parseFloat(margin) > 20
            ? "gold"
            : parseFloat(margin) > 0
            ? "orange"
            : "red";

        return (
          <div>
            <Text strong>ZMW {val.toLocaleString()}</Text>
            <div>
              <Tag color={color}>{margin}% Margin</Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: "Performance",
      render: (_, record) => {
        // Calculate ROI only if totalLoans > 0 to avoid division by zero
        const roi =
          record.totalLoans > 0
            ? ((record.netProfit / record.totalLoans) * 100).toFixed(1)
            : 0;

        return (
          <Progress
            percent={roi}
            status="active"
            strokeColor={
              parseFloat(roi) > 15
                ? "#52c41a" // green for good ROI
                : parseFloat(roi) > 0
                ? "#faad14" // yellow for moderate ROI
                : "#ff4d4f" // red for negative ROI
            }
          />
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {guideTipVisible && (
        <Alert
          message="Feature: Profitability Analysis"
          description={
            <Space direction="vertical">
              <Text>
                This tool helps you analyze the profitability of your loan
                portfolio. Click the help button to learn more.
              </Text>
              <Button
                type="primary"
                size="small"
                onClick={() => setHelpVisible(true)}
              >
                View Guide
              </Button>
            </Space>
          }
          type="info"
          showIcon
          icon={<BulbOutlined />}
          closable
          onClose={() => setGuideTipVisible(false)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
          className="profit-header"
        >
          <Col>
            <Title level={3} style={{ marginBottom: 0 }}>
              <FundOutlined /> Profitability Analysis
              <HelpTooltip title="View the Profitability Analysis guide">
                <BookOutlined
                  style={{
                    fontSize: 16,
                    marginLeft: 8,
                    color: "#1890ff",
                    cursor: "pointer",
                  }}
                  onClick={() => setHelpVisible(true)}
                />
              </HelpTooltip>
            </Title>
            <Text type="secondary">{company.name} • Financial Performance</Text>
          </Col>
          <Col className="date-range">
            <RangePicker
              defaultValue={dateRange}
              onChange={setDateRange}
              style={{ width: 256 }}
            />
            <Tooltip
              title="Take a guided tour of this page"
              visible={showTip === "tour"}
            >
              <Button
                style={{ marginLeft: 16 }}
                icon={<VideoCameraOutlined />}
                onClick={() => setShowTour(true)}
                onMouseEnter={() => setShowTip("tour")}
                onMouseLeave={() => setShowTip(null)}
              >
                Tour
              </Button>
            </Tooltip>
            <Button style={{ marginLeft: 8 }} icon={<PieChartOutlined />}>
              Export Report
            </Button>
          </Col>
        </Row>

        {/* Key Metrics */}
        <Row gutter={16} style={{ marginBottom: 24 }} className="key-metrics">
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Net Interest Margin"
                value={profitMetrics.netInterestMargin}
                suffix="%"
                valueStyle={{ color: "#52c41a", fontSize: 24 }}
                prefix={<BankOutlined />}
              />
              <Text type="secondary">Industry Avg: 15.2%</Text>
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Return on Assets"
                value={profitMetrics.returnOnAssets}
                suffix="%"
                valueStyle={{ color: "#1890ff", fontSize: 24 }}
                prefix={<PieChartOutlined />}
              />
              <Text type="secondary">
                {profitMetrics.returnOnAssets > 4.0
                  ? "Above Target"
                  : "Below Target"}
              </Text>
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Cost to Income"
                value={profitMetrics.costToIncome}
                suffix="%"
                valueStyle={{
                  color:
                    profitMetrics.costToIncome > 40 ? "#ff4d4f" : "#52c41a",
                  fontSize: 24,
                }}
                prefix={<InfoCircleOutlined />}
              />
              <Text type="secondary">Operating Efficiency</Text>
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Risk-Adjusted Margin"
                value={profitMetrics.riskAdjustedMargin}
                suffix="%"
                valueStyle={{ 
                  color: profitMetrics.riskAdjustedMargin >= 12 
                    ? "#52c41a"  // green for strong (>12%)
                    : profitMetrics.riskAdjustedMargin >= 8 
                      ? "#faad14" // gold/orange for adequate (8-12%)
                      : "#ff4d4f", // red for concerning (<8%)
                  fontSize: 24 
                }}
                prefix={<DollarOutlined />}
              />
              <Text type="secondary" style={{
                color: profitMetrics.riskAdjustedMargin >= 12 
                  ? "#52c41a"  // green for strong (>12%)
                  : profitMetrics.riskAdjustedMargin >= 8 
                    ? "#faad14" // gold/orange for adequate (8-12%)
                    : "#ff4d4f" // red for concerning (<8%)
              }}>
                {profitMetrics.riskAdjustedMargin >= 12 
                  ? "Strong" 
                  : profitMetrics.riskAdjustedMargin >= 8 
                    ? "Adequate" 
                    : "Concerning"}
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Main Charts */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={16}>
            <Card
              title="Revenue vs Costs Trend"
              bordered={false}
              className="revenue-chart"
            >
              <Line
                data={revenueChart}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: "top" },
                    tooltip: {
                      callbacks: {
                        label: (context) =>
                          `ZMW ${context.parsed.y.toLocaleString()}K`,
                      },
                    },
                  },
                  scales: {
                    y: {
                      title: { text: "ZMW (Thousands)", display: true },
                      ticks: { callback: (value) => `ZMW ${value}K` },
                    },
                  },
                }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card
              title="Cost Breakdown"
              bordered={false}
              className="cost-breakdown"
            >
              <div style={{ position: "relative", height: "300px" }}>
                <Pie
                  data={{
                    labels: Object.keys(costBreakdown),
                    datasets: [
                      {
                        data: Object.values(costBreakdown),
                        backgroundColor: [
                          "#1890ff",
                          "#52c41a",
                          "#ff4d4f",
                          "#722ed1",
                        ],
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: { position: "bottom" },
                      tooltip: {
                        callbacks: {
                          label: (context) =>
                            `${context.label}: ${context.raw}% of total costs`,
                        },
                      },
                    },
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>

        {/* Product Performance */}
        <Card
          title="Loan Product Profitability"
          bordered={false}
          className="product-table"
          extra={
            <div>
              <Select
                defaultValue="all"
                onChange={setProductFilter}
                style={{ width: 160, marginRight: 8 }}
                loading={loading}
              >
                <Option value="all">All Products</Option>
                {loanProducts.map((product) => (
                  <Option key={product.id} value={product.loan_name}>
                    {product.loan_name}
                  </Option>
                ))}
              </Select>
              <Select
                defaultValue="all"
                onChange={setTermFilter}
                style={{ width: 160 }}
                loading={loading}
              >
                <Option value="all">All Terms</Option>
                {/* Extract unique terms from profitData */}
                {[...new Set(profitData.map((item) => item.term))].map(
                  (term) => (
                    <Option key={term} value={term}>
                      {term}
                    </Option>
                  )
                )}
              </Select>
            </div>
          }
        >
          <Table
            columns={columns}
            dataSource={profitData}
            rowKey="id"
            pagination={false}
            summary={() => (
              <Table.Summary>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Text strong>
                      ZMW{" "}
                      {profitData
                        .reduce((sum, p) => sum + p.totalLoans, 0)
                        .toLocaleString()}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <Text strong>
                      ZMW{" "}
                      {profitData
                        .reduce((sum, p) => sum + p.interestIncome, 0)
                        .toLocaleString()}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <Text strong>
                      ZMW{" "}
                      {profitData
                        .reduce((sum, p) => sum + p.operatingCosts, 0)
                        .toLocaleString()}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <Text strong>
                      ZMW{" "}
                      {profitData
                        .reduce((sum, p) => sum + p.chargeOffs, 0)
                        .toLocaleString()}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    <Text strong>
                      ZMW{" "}
                      {profitData
                        .reduce((sum, p) => sum + p.netProfit, 0)
                        .toLocaleString()}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>

        {/* Methodology Card */}
        <Card
          title="Calculation Methodology"
          bordered={false}
          style={{ marginTop: 24 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Net Interest Margin Formula:</Text>
                <Text code>
                  (Interest Income - Interest Expense) / Average Earning Assets
                </Text>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Risk-Adjusted Margin:</Text>
                <Text>
                  Includes provision for loan losses (3Y average charge-off
                  rate)
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Cost Allocation:</Text>
                <ul>
                  <li>Operational: Branch costs, salaries, technology</li>
                  <li>Funding: Cost of capital, interest expenses</li>
                  <li>Risk: Loan loss provisions, insurance</li>
                  <li>Acquisition: Marketing, sales commissions</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Card>
      </Card>

      {/* Help Modal */}
      {renderHelpModal()}

      {/* Tour Component */}
      {renderTourStep()}
    </div>
  );
};

export default ProfitabilityAnalysis;
